import { AlertCircle, BarChart3, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { StatsArtefact } from './extractArtefacts';

/**
 * Renders a `getDashboardStats` snapshot — a quick "where am I?" KPI
 * read on outstanding / overdue / paid / drafts plus a tap-through to
 * the biggest debtor.
 *
 * Older snapshots in the chat tape (the operator asked for "this
 * week" then "this month") render the same; we don't grey them out
 * because each one is anchored to the question that produced it and
 * still answers that question correctly.
 */
export interface StatsCardProps {
  stats: StatsArtefact;
}

export function StatsCard({ stats }: StatsCardProps) {
  return (
    <article className="overflow-hidden rounded-2xl border border-line bg-card shadow-sm shadow-black/5">
      <div className="h-[3px] bg-accent" />

      <header className="flex items-center gap-3 px-4 pb-3 pt-3.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent">
          <BarChart3 className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Snapshot</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{stats.periodLabel}</p>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 px-3 pb-3">
        <Tile label="Outstanding" value={stats.outstanding.amountFormatted} count={stats.outstanding.count} />
        <Tile
          label={`Overdue${
            stats.overdue.oldestDays !== null && stats.overdue.oldestDays > 0
              ? ` · ${stats.overdue.oldestDays}d`
              : ''
          }`}
          value={stats.overdue.amountFormatted}
          count={stats.overdue.count}
          tone={stats.overdue.count > 0 ? 'warning' : 'default'}
        />
        <Tile
          label={`Paid · ${stats.periodLabel}`}
          value={stats.paidInPeriod.amountFormatted}
          count={stats.paidInPeriod.count}
          tone="success"
        />
        <Tile
          label="Drafts"
          value={stats.drafts.amountFormatted}
          count={stats.drafts.count}
        />
      </div>

      {stats.topOutstandingClient ? (
        <Link
          to={`/clients?q=${encodeURIComponent(stats.topOutstandingClient.name)}`}
          className="flex items-center gap-3 border-t border-line px-4 py-3 transition-colors hover:bg-secondary/40"
        >
          <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-warning-soft text-warning">
            <AlertCircle className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium text-foreground">
              {stats.topOutstandingClient.name}
            </p>
            <p className="text-[11px] text-muted-foreground">
              {stats.topOutstandingClient.invoiceCount}{' '}
              invoice{stats.topOutstandingClient.invoiceCount === 1 ? '' : 's'} ·{' '}
              {stats.topOutstandingClient.amountFormatted}
            </p>
          </div>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        </Link>
      ) : null}
    </article>
  );
}

function Tile({
  label,
  value,
  count,
  tone = 'default',
}: {
  label: string;
  value: string;
  count: number;
  tone?: 'default' | 'success' | 'warning';
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-line bg-card px-3 py-2.5',
        tone === 'success' && 'bg-success-soft/40 border-success/15',
        tone === 'warning' && 'bg-warning-soft/40 border-warning/20',
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 font-display text-lg font-semibold tracking-tight text-foreground tabular-nums">
        {value}
      </p>
      <p className="text-[10px] text-muted-foreground">
        {count} invoice{count === 1 ? '' : 's'}
      </p>
    </div>
  );
}

